import React from 'react';
import { useForm } from 'react-final-form';
import { Col, Row } from '@estimateone/frontend-components';
import { Heading } from '../../../../fec';
import { QuoteFile as QuoteFileCard } from '../../QuoteFiles';
import QuoteAmountField from './QuoteAmountField';
import QuoteDetailsField from './QuoteDetailsField';
import QuoteExtraFilesField from './QuoteExtraFilesField';
import QuoteFileField from './QuoteFileField';
import { FileManagerParams, LegacyFileManagerParams } from '@shared/FileUploader/types';
import { QuoteFormCommonFieldName } from '@ascension/enums';
import type { ExtraFilesServerlessFileManagerParams, QuoteFormsValues } from '../types';
import { DownloadableQuoteFile } from '@api/fragments/Quotes/types';
import styles from '../styles.scss';

type QuoteFormQuoteSectionProps = LegacyFileManagerParams &
  ExtraFilesServerlessFileManagerParams & {
    quoteFileField: React.ReactElement<QuoteFileField> | React.ReactElement<typeof QuoteFileCard>;
  };

const QuoteFormQuoteSection = ({
  fileManagerEndpoint,
  fileManagerKey,
  fileManagerLocale,
  extraFilesParentType,
  extraFilesParentId,
  quoteFileField,
}: QuoteFormQuoteSectionProps) => {
  const { getState } = useForm<QuoteFormsValues>();
  const { values } = getState();

  return (
    <>
      <div className={styles.sectionHeading}>
        <Heading level={2}>Your quote</Heading>
      </div>

      <Row>
        <Col span={12}>
          <QuoteAmountField />
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <div className={styles.section}>{quoteFileField}</div>
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <div
            className={
              values[QuoteFormCommonFieldName.ExtraFiles]?.length ? styles.section : undefined
            }
          >
            <QuoteExtraFilesField
              fileManagerEndpoint={fileManagerEndpoint}
              fileManagerKey={fileManagerKey}
              fileManagerLocale={fileManagerLocale}
              parentType={extraFilesParentType}
              parentId={extraFilesParentId}
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <QuoteDetailsField />
        </Col>
      </Row>
    </>
  );
};

export type QuoteFormQuoteSectionWithoutExtraFilesProps = FileManagerParams;

const QuoteFormQuoteSectionWithoutExtraFiles = ({
  fileManagerEndpoint,
  fileManagerKey,
  fileManagerLocale,
  parentId,
  parentType,
}: QuoteFormQuoteSectionWithoutExtraFilesProps) => (
  <>
    <div className={styles.sectionHeading}>
      <Heading level={4}>Your quote</Heading>
    </div>
    <Row>
      <Col span={12}>
        <QuoteAmountField />
      </Col>
    </Row>
    <Row>
      <Col span={12}>
        <div className={styles.section}>
          <QuoteFileField
            fileManagerEndpoint={fileManagerEndpoint}
            fileManagerKey={fileManagerKey}
            fileManagerLocale={fileManagerLocale}
            parentId={parentId}
            parentType={parentType}
          />
        </div>
      </Col>
    </Row>
    <Row>
      <Col span={12}>
        <QuoteDetailsField />
      </Col>
    </Row>
  </>
);

type QuoteSectionWithExistingQuoteFileProps = LegacyFileManagerParams &
  ExtraFilesServerlessFileManagerParams & { quoteFile: DownloadableQuoteFile };

const QuoteSectionWithExistingQuoteFile = ({
  quoteFile,
  ...sectionProps
}: QuoteSectionWithExistingQuoteFileProps) => (
  <QuoteFormQuoteSection quoteFileField={<QuoteFileCard file={quoteFile} />} {...sectionProps} />
);

type QuoteSectionWithQuoteFileUploaderProps = FileManagerParams &
  ExtraFilesServerlessFileManagerParams;

const QuoteSectionWithQuoteFileUploader = ({
  fileManagerEndpoint,
  fileManagerKey,
  fileManagerLocale,
  parentType,
  parentId,
  extraFilesParentType,
  extraFilesParentId,
}: QuoteSectionWithQuoteFileUploaderProps) => (
  <QuoteFormQuoteSection
    fileManagerEndpoint={fileManagerEndpoint}
    fileManagerKey={fileManagerKey}
    fileManagerLocale={fileManagerLocale}
    extraFilesParentType={extraFilesParentType}
    extraFilesParentId={extraFilesParentId}
    quoteFileField={
      <QuoteFileField
        fileManagerEndpoint={fileManagerEndpoint}
        fileManagerKey={fileManagerKey}
        fileManagerLocale={fileManagerLocale}
        parentType={parentType}
        parentId={parentId}
      />
    }
  />
);

export type QuoteFileField = typeof QuoteFileField;

export {
  QuoteAmountField,
  QuoteDetailsField,
  QuoteExtraFilesField,
  QuoteFormQuoteSectionWithoutExtraFiles,
  QuoteSectionWithExistingQuoteFile,
  QuoteSectionWithQuoteFileUploader,
};
